import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Scissors, Sparkles, MapPin, MessageSquare, Bot, CalendarDays } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1559190394-df5a28aab5c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxtb2JpbGUlMjBkb2clMjBncm9vbWluZyUyMHZhbiUyMGRvZyUyMGdyb29tZXIlMjBoYXBweSUyMGRvZ3xlbnwwfHx8fDE3ODk4NjQwNTN8MA&ixlib=rb-4.1.0&q=85";
const IMG2 = "https://images.pexels.com/photos/6131150/pexels-photo-6131150.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG3 = "https://images.unsplash.com/photo-1651273427958-bf78352e39be?w=800";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1F1A17]">
      {/* NAV */}
      <header className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-xl bg-[#C85A17] grid place-content-center text-white shadow-sm">
            <Scissors className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-bold tracking-tight">PawTrek</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-login">
            <Button variant="ghost" className="rounded-full">Sign in</Button>
          </Link>
          <Link to="/register" data-testid="nav-signup">
            <Button className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile">
              Get started
            </Button>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-6 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 space-y-6">
          <div className="pill bg-[#F5E6CF] text-[#A9480F] border border-amber-900/10 w-fit">
            <Sparkles className="w-3 h-3" /> Mobile grooming, simplified
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] tracking-tight">
            Doorstep spa care.<br/>
            <span className="text-[#C85A17]">Zero</span> travel stress.
          </h1>
          <p className="text-base sm:text-lg text-[#6B625B] max-w-xl leading-relaxed">
            PawTrek is the two-sided companion for mobile dog groomers. Manage your
            route, chat with clients, and let AI draft the message when your hands
            are full of fluff.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/signup/groomer" data-testid="cta-groomer">
              <Button size="lg" className="rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile px-6">
                I'm a groomer · Start free
              </Button>
            </Link>
            <Link to="/register?role=client" data-testid="cta-client">
              <Button size="lg" variant="outline"
                className="rounded-full border-[#2D4A3E] text-[#2D4A3E] hover:bg-[#2D4A3E] hover:text-white btn-tactile px-6">
                Book my pup
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-6 pt-2 text-sm text-[#6B625B]">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4"/> Route-aware</span>
            <span className="flex items-center gap-1.5"><MessageSquare className="w-4 h-4"/> Two-way chat</span>
            <span className="flex items-center gap-1.5"><Bot className="w-4 h-4"/> AI drafts</span>
          </div>
        </div>

        <div className="lg:col-span-6 grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
          <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden shadow-lg relative">
            <img src={HERO} alt="mobile dog groomer" className="w-full h-full object-cover"/>
            <div className="absolute bottom-4 left-4 glass rounded-2xl px-4 py-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#2D4A3E] grid place-content-center text-white">
                <CalendarDays className="w-4 h-4"/>
              </div>
              <div className="text-sm">
                <div className="font-semibold">Next stop · 10:30 AM</div>
                <div className="text-xs text-[#6B625B]">Biscuit · Full Groom</div>
              </div>
            </div>
          </div>
          <div className="col-span-2 row-span-2 rounded-3xl overflow-hidden shadow-md">
            <img src={IMG3} alt="fluffy dog" className="w-full h-full object-cover"/>
          </div>
          <div className="col-span-2 row-span-2 rounded-3xl bg-[#2D4A3E] text-white p-4 flex flex-col justify-between shadow-md">
            <span className="label-accent text-amber-300">Today</span>
            <div>
              <div className="font-display text-3xl font-bold">7 stops</div>
              <div className="text-xs opacity-80">$685 booked</div>
            </div>
          </div>
          <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden shadow-md">
            <img src={IMG2} alt="groomer washing dog" className="w-full h-full object-cover"/>
          </div>
          <div className="col-span-2 row-span-2 rounded-3xl bg-[#E8A838] p-4 flex flex-col justify-between shadow-md">
            <span className="label-accent text-[#1F1A17]">AI reply</span>
            <p className="text-[#1F1A17] text-sm font-medium leading-snug">
              "Max was a champ today — he loved the blueberry facial!"
            </p>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-4">
        {[
          { icon: CalendarDays, title: "Schedule your route", copy: "Day / week / route views with drive-time buffers between every stop." },
          { icon: MessageSquare, title: "Chat in real time", copy: "One thread per client. Photos, ETAs, and quick replies built for the truck." },
          { icon: Bot, title: "AI co-pilot", copy: "Draft replies and summarize grooming notes in a tap. Powered by Claude." },
        ].map(({ icon: Icon, title, copy }, i) => (
          <div key={i} className="p-6 rounded-3xl bg-white border border-[#E5DCCE] card-tactile shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-[#F5E6CF] text-[#C85A17] grid place-content-center mb-4">
              <Icon className="w-5 h-5" strokeWidth={2.2}/>
            </div>
            <h3 className="font-display text-xl font-bold mb-1">{title}</h3>
            <p className="text-sm text-[#6B625B] leading-relaxed">{copy}</p>
          </div>
        ))}
      </section>

      <footer className="border-t border-[#E5DCCE] mt-10">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-[#6B625B] flex items-center justify-between">
          <span>© 2026 PawTrek Mobile Grooming</span>
          <span>Built for groomers who show up.</span>
        </div>
      </footer>
    </div>
  );
}
